/**
 * Video Popup Component
 * Handles video popup with HLS support, progress bar, controls, and autoplay next
 */
class VideoPopup extends HTMLElement {
  constructor() {
    super();
    this.video = null;
    this.hlsInstance = null;
    this.currentVideoIndex = -1;
    this.videoItems = [];
    this.isPlaying = false;
    this.isMuted = false;
    this.autoplay = true;
    this.muted = true;
    this.showControls = true;
    this.autoplayNext = true;
    
    this.handleVideoEnd = this.handleVideoEnd.bind(this);
    this.handleTimeUpdate = this.handleTimeUpdate.bind(this);
    this.handleLoadedMetadata = this.handleLoadedMetadata.bind(this);
  }

  connectedCallback() {
    this.video = this.querySelector('.video-popup__video');
    if (!this.video) return;
    
    // Get settings from data attributes
    this.autoplay = this.dataset.autoplay === 'true';
    this.muted = this.dataset.muted === 'true';
    this.showControls = this.dataset.showControls === 'true';
    this.autoplayNext = this.dataset.autoplayNext === 'true';
    
    // Setup video
    if (this.muted) {
      this.video.muted = true;
      this.isMuted = true;
    }
    
    // Setup event listeners
    this.setupEventListeners();
    
    // Setup controls
    this.setupControls();
    
    // Find parent shop-by-look container
    const shopByLookContainer = this.closest('.product__block--shop_by_look');
    if (shopByLookContainer) {
      this.videoItems = Array.from(shopByLookContainer.querySelectorAll('.shop-by-look__item[data-media-type="video"]'));
      this.setupVideoTriggers();
    }
  }

  disconnectedCallback() {
    this.cleanup();
  }

  setupEventListeners() {
    // Video events
    this.video.addEventListener('ended', this.handleVideoEnd);
    this.video.addEventListener('timeupdate', this.handleTimeUpdate);
    this.video.addEventListener('loadedmetadata', this.handleLoadedMetadata);
    this.video.addEventListener('play', () => {
      this.isPlaying = true;
      this.updatePlayPauseButton();
    });
    this.video.addEventListener('pause', () => {
      this.isPlaying = false;
      this.updatePlayPauseButton();
    });
    this.video.addEventListener('volumechange', () => {
      this.isMuted = this.video.muted;
      this.updateMuteButton();
    });
    
    // Close button
    const closeBtn = this.querySelector('.video-popup__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.close());
    }
    
    // Overlay click
    const overlay = this.querySelector('.fixed-overlay');
    if (overlay) {
      overlay.addEventListener('click', () => this.close());
    }
    
    // Keyboard events
    document.addEventListener('keydown', (e) => {
      if (this.hasAttribute('open')) {
        if (e.key === 'Escape') {
          this.close();
        } else if (e.key === ' ') {
          e.preventDefault();
          this.togglePlayPause();
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.playPrevious();
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.playNext();
        } else if (e.key === 'm' || e.key === 'M') {
          e.preventDefault();
          this.toggleMute();
        }
      }
    });
  }

  setupControls() {
    // Play/Pause button
    const playPauseBtn = this.querySelector('.video-popup__play-pause');
    if (playPauseBtn) {
      playPauseBtn.addEventListener('click', () => this.togglePlayPause());
    }
    
    // Mute button
    const muteBtn = this.querySelector('.video-popup__mute');
    if (muteBtn) {
      muteBtn.addEventListener('click', () => this.toggleMute());
    }
    
    // Next/Previous buttons
    const nextBtn = this.querySelector('.video-popup__next');
    if (nextBtn) {
      nextBtn.addEventListener('click', () => this.playNext());
    }
    
    const prevBtn = this.querySelector('.video-popup__prev');
    if (prevBtn) {
      prevBtn.addEventListener('click', () => this.playPrevious());
    }
    
    // Progress bar click
    const progressBar = this.querySelector('.video-popup__progress-bar');
    if (progressBar) {
      progressBar.addEventListener('click', (e) => {
        const rect = progressBar.getBoundingClientRect();
        const percent = (e.clientX - rect.left) / rect.width;
        this.seekTo(percent);
      });
    }
  }

  setupVideoTriggers() {
    this.videoItems.forEach((item, index) => {
      const trigger = item.querySelector('[data-video-trigger]');
      if (trigger) {
        trigger.addEventListener('click', () => {
          this.openWithVideo(item, index);
        });
      }
    });
  }

  async openWithVideo(item, index) {
    const videoSrc = item.dataset.videoSrc || '';
    const videoHls = item.dataset.videoHls || '';
    
    if (!videoSrc && !videoHls) {
      console.warn('No video source found');
      return;
    }
    
    this.currentVideoIndex = index;
    this.setAttribute('open', '');
    document.body.style.overflow = 'hidden';
    
    // Reset video
    this.video.pause();
    this.video.currentTime = 0;
    
    // Load video with HLS support
    await this.loadVideo(videoHls || videoSrc, videoSrc);
    
    // Play if autoplay is enabled
    if (this.autoplay) {
      try {
        await this.video.play();
      } catch (e) {
        console.warn('Autoplay prevented:', e);
      }
    }
    
    // Update navigation buttons
    this.updateNavigationButtons();
  }

  async loadVideo(hlsSrc, fallbackSrc) {
    // Cleanup previous HLS instance
    this.cleanupHls();
    
    // Check if browser supports native HLS (Safari)
    if (this.video.canPlayType('application/vnd.apple.mpegurl')) {
      this.video.src = hlsSrc;
      this.video.hlsInstance = { isNative: true };
    } else if (typeof Hls !== 'undefined' && Hls.isSupported()) {
      // Use HLS.js for non-Safari browsers
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
        backBufferLength: 90,
        maxBufferLength: 30,
        maxMaxBufferLength: 60,
        maxBufferSize: 60 * 1000 * 1000,
        maxBufferHole: 0.5,
        highBufferWatchdogPeriod: 2,
        nudgeOffset: 0.1,
        nudgeMaxRetry: 3,
        maxFragLoadingTimeOut: 200000,
        maxLoadingDelay: 4,
        minAutoBitrate: 0,
        emeEnabled: false
      });
      
      hls.loadSource(hlsSrc);
      hls.attachMedia(this.video);
      this.hlsInstance = hls;
      this.video.hlsInstance = hls;
      
      // Handle HLS errors
      hls.on(Hls.Events.ERROR, (event, data) => {
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              console.error('Fatal network error, trying to recover');
              if (data.response && data.response.code === 404 && fallbackSrc) {
                hls.destroy();
                this.video.src = fallbackSrc;
                this.video.hlsInstance = null;
              } else {
                hls.startLoad();
              }
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              console.error('Fatal media error, trying to recover');
              hls.recoverMediaError();
              break;
            default:
              console.error('Fatal error, cannot recover');
              if (fallbackSrc) {
                hls.destroy();
                this.video.src = fallbackSrc;
                this.video.hlsInstance = null;
              }
              break;
          }
        }
      });
    } else {
      // Fallback to regular video
      this.video.src = fallbackSrc || hlsSrc;
    }
    
    // Set data attributes for HLS initialization script
    if (hlsSrc) {
      this.video.setAttribute('data-hls-src', hlsSrc);
    }
    if (fallbackSrc) {
      this.video.setAttribute('data-original-src', fallbackSrc);
    }
  }

  cleanupHls() {
    if (this.hlsInstance && !this.hlsInstance.isNative) {
      this.hlsInstance.destroy();
      this.hlsInstance = null;
    }
    if (this.video && this.video.hlsInstance && !this.video.hlsInstance.isNative) {
      this.video.hlsInstance.destroy();
      this.video.hlsInstance = null;
    }
  }

  cleanup() {
    this.cleanupHls();
    if (this.video) {
      this.video.pause();
      this.video.src = '';
    }
  }

  close() {
    this.removeAttribute('open');
    document.body.style.overflow = '';
    if (this.video) {
      this.video.pause();
    }
  }

  togglePlayPause() {
    if (this.video.paused) {
      this.video.play();
    } else {
      this.video.pause();
    }
  }

  toggleMute() {
    this.video.muted = !this.video.muted;
    this.isMuted = this.video.muted;
    this.updateMuteButton();
  }

  seekTo(percent) {
    if (this.video.duration) {
      this.video.currentTime = this.video.duration * percent;
    }
  }

  playNext() {
    if (this.currentVideoIndex < this.videoItems.length - 1) {
      this.openWithVideo(this.videoItems[this.currentVideoIndex + 1], this.currentVideoIndex + 1);
    }
  }

  playPrevious() {
    if (this.currentVideoIndex > 0) {
      this.openWithVideo(this.videoItems[this.currentVideoIndex - 1], this.currentVideoIndex - 1);
    }
  }

  updatePlayPauseButton() {
    const playIcon = this.querySelector('.video-popup__play-icon');
    const pauseIcon = this.querySelector('.video-popup__pause-icon');
    
    if (this.isPlaying) {
      playIcon?.classList.add('hidden');
      pauseIcon?.classList.remove('hidden');
    } else {
      playIcon?.classList.remove('hidden');
      pauseIcon?.classList.add('hidden');
    }
  }

  updateMuteButton() {
    const muteIcon = this.querySelector('.video-popup__mute-icon');
    const unmuteIcon = this.querySelector('.video-popup__unmute-icon');
    
    if (this.isMuted) {
      muteIcon?.classList.add('hidden');
      unmuteIcon?.classList.remove('hidden');
    } else {
      muteIcon?.classList.remove('hidden');
      unmuteIcon?.classList.add('hidden');
    }
  }

  updateNavigationButtons() {
    const nextBtn = this.querySelector('.video-popup__next');
    const prevBtn = this.querySelector('.video-popup__prev');
    
    if (nextBtn) {
      nextBtn.disabled = this.currentVideoIndex >= this.videoItems.length - 1;
      nextBtn.style.opacity = nextBtn.disabled ? '0.5' : '1';
    }
    
    if (prevBtn) {
      prevBtn.disabled = this.currentVideoIndex <= 0;
      prevBtn.style.opacity = prevBtn.disabled ? '0.5' : '1';
    }
  }

  handleVideoEnd() {
    if (this.autoplayNext && this.currentVideoIndex < this.videoItems.length - 1) {
      // Auto-play next video
      setTimeout(() => {
        this.playNext();
      }, 500);
    }
  }

  handleTimeUpdate() {
    if (!this.video.duration) return;
    
    const currentTime = this.video.currentTime;
    const duration = this.video.duration;
    const percent = (currentTime / duration) * 100;
    
    // Update progress bar
    const progressFill = this.querySelector('.video-popup__progress-fill');
    if (progressFill) {
      progressFill.style.width = `${percent}%`;
    }
    
    // Update time display
    const currentTimeEl = this.querySelector('.video-popup__current-time');
    const durationEl = this.querySelector('.video-popup__duration');
    
    if (currentTimeEl) {
      currentTimeEl.textContent = this.formatTime(currentTime);
    }
    if (durationEl) {
      durationEl.textContent = this.formatTime(duration);
    }
  }

  handleLoadedMetadata() {
    // Update duration when metadata is loaded
    const durationEl = this.querySelector('.video-popup__duration');
    if (durationEl && this.video.duration) {
      durationEl.textContent = this.formatTime(this.video.duration);
    }
  }

  formatTime(seconds) {
    if (!seconds || isNaN(seconds)) return '0:00';
    
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }
}

customElements.define('video-popup', VideoPopup);
